import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import AppNav from '../AppNav/AppNav';

const DEFAULT_TRANSLATION_ADJUSTMENT_PROMPT =
  'Make the translation a bit more natural while preserving the Japanese meaning closely.';
const OA_PROMPT_MEMORY_KEY = 'pm.oa_refine_prompt_memory.v1';

const initialJob = {
  path: '',
  title: '',
  job_kind: '',
  status: '',
  matter_reference_code: '',
  application_number: '',
  matter_title: '',
  matter_path: '',
  office_action_path: '',
};

const initialOA = {
  path: '',
  title: '',
  oa_date: '',
  drafting_date: '',
  original_text: '',
  translation: '',
  confirmed_translation: '',
  response_draft: '',
  client_report_draft: '',
  oa_profile_path: '',
};

export default function OAWorkspacePage() {
  const token = useSelector((state) => state.userSession?.token);
  const jobPath = useMemo(() => {
    if (typeof window === 'undefined') {
      return '';
    }
    const params = new URLSearchParams(window.location.search);
    return normalizeContentPath(params.get('path') || '');
  }, []);

  const [job, setJob] = useState(initialJob);
  const [oa, setOA] = useState(initialOA);
  const [loading, setLoading] = useState(true);
  const [creatingWorkspace, setCreatingWorkspace] = useState(false);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [refiningTranslation, setRefiningTranslation] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [loadingJPODocuments, setLoadingJPODocuments] = useState(false);
  const [importingJPODocument, setImportingJPODocument] = useState('');
  const [jpoDocuments, setJPODocuments] = useState([]);
  const [applicationNumberDraft, setApplicationNumberDraft] = useState('');
  const [translationAdjustmentPrompt, setTranslationAdjustmentPrompt] = useState(
    DEFAULT_TRANSLATION_ADJUSTMENT_PROMPT,
  );
  const [oaProfiles, setOAProfiles] = useState([]);
  const [loadingProfiles, setLoadingProfiles] = useState(true);
  const [recentAdjustmentPrompts, setRecentAdjustmentPrompts] = useState([]);
  const [savedAdjustmentPresets, setSavedAdjustmentPresets] = useState([]);
  const [confirmingTranslation, setConfirmingTranslation] = useState(false);
  const [generatingClientReport, setGeneratingClientReport] = useState(false);
  const [exportingDocx, setExportingDocx] = useState('');
  const [savingApplicationNumber, setSavingApplicationNumber] = useState(false);
  const [translationElapsedSeconds, setTranslationElapsedSeconds] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const translationBusy = translating || refiningTranslation;
  const promptScopeKey = useMemo(
    () => getPromptScopeKey(oa.oa_profile_path),
    [oa.oa_profile_path],
  );
  const sortedJPODocuments = useMemo(() => {
    return [...jpoDocuments].sort((left, right) => {
      const leftPriority =
        left.downloadable && left.importable ? 0 : left.downloadable ? 1 : 2;
      const rightPriority =
        right.downloadable && right.importable ? 0 : right.downloadable ? 1 : 2;
      if (leftPriority !== rightPriority) {
        return leftPriority - rightPriority;
      }
      return String(right.date || '').localeCompare(String(left.date || ''));
    });
  }, [jpoDocuments]);
  const translationBusyLabel = translating
    ? 'Generating translation'
    : refiningTranslation
      ? 'Refining translation'
      : '';

  useEffect(() => {
    document.body.classList.add('patent-dashboard-page');
    return () => {
      document.body.classList.remove('patent-dashboard-page');
    };
  }, []);

  function authHeaders(extra = {}) {
    return token
      ? {
          ...extra,
          Authorization: `Bearer ${token}`,
        }
      : extra;
  }

  function scopePayload(requireOfficeAction = false) {
    if (requireOfficeAction && oa.path) {
      return {
        matter_path: job.matter_path,
        office_action_path: oa.path,
      };
    }
    return {
      matter_path: job.matter_path,
      ...(oa.path ? { office_action_path: oa.path } : {}),
    };
  }

  useEffect(() => {
    let active = true;

    async function loadProfiles() {
      try {
        const response = await fetch('/++api++/@oa-profiles', {
          headers: authHeaders({ Accept: 'application/json' }),
        });
        if (!response.ok) {
          throw new Error(`OA profile lookup failed with status ${response.status}`);
        }
        const payload = await response.json();
        if (active) {
          setOAProfiles(payload.items || []);
        }
      } catch (loadError) {
        if (active) {
          setOAProfiles([]);
        }
      } finally {
        if (active) {
          setLoadingProfiles(false);
        }
      }
    }

    loadProfiles();
    return () => {
      active = false;
    };
  }, [token]);

  useEffect(() => {
    if (!translationBusy) {
      setTranslationElapsedSeconds(0);
      return undefined;
    }

    setTranslationElapsedSeconds(0);
    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      setTranslationElapsedSeconds(
        Math.max(1, Math.floor((Date.now() - startedAt) / 1000)),
      );
    }, 1000);

    return () => {
      window.clearInterval(timer);
    };
  }, [translationBusy]);

  useEffect(() => {
    const memory = readPromptMemory(promptScopeKey);
    setRecentAdjustmentPrompts(memory.recent);
    setSavedAdjustmentPresets(memory.presets);
  }, [promptScopeKey]);

  useEffect(() => {
    let active = true;

    async function load() {
      if (!jobPath) {
        setError('No job path was provided.');
        setLoading(false);
        return;
      }

      try {
        const jobResponse = await fetch(`/++api++${jobPath}`, {
          headers: authHeaders({ Accept: 'application/json' }),
        });
        if (!jobResponse.ok) {
          throw new Error(`Job lookup failed with status ${jobResponse.status}`);
        }
        const jobPayload = await jobResponse.json();
        let normalizedJob = normalizeJob(jobPath, jobPayload);

        if (normalizedJob.matter_path) {
          const matterResponse = await fetch(`/++api++${normalizedJob.matter_path}`, {
            headers: authHeaders({ Accept: 'application/json' }),
          });
          if (matterResponse.ok) {
            const matterPayload = await matterResponse.json();
            normalizedJob = {
              ...normalizedJob,
              application_number:
                matterPayload.application_number || normalizedJob.application_number,
              matter_reference_code:
                matterPayload.matter_reference_code ||
                matterPayload.matter_reference ||
                normalizedJob.matter_reference_code,
              matter_title: matterPayload.title || normalizedJob.matter_title,
            };
          }
        }

        let normalizedOA = initialOA;
        if (normalizedJob.office_action_path) {
          const oaResponse = await fetch(`/++api++${normalizedJob.office_action_path}`, {
            headers: authHeaders({ Accept: 'application/json' }),
          });
          if (oaResponse.ok) {
            const oaPayload = await oaResponse.json();
            normalizedOA = normalizeOfficeAction(
              normalizedJob.office_action_path,
              oaPayload,
            );
          }
        }

        if (active) {
          setJob(normalizedJob);
          setOA(normalizedOA);
          setApplicationNumberDraft(normalizedJob.application_number || '');
        }
      } catch (loadError) {
        if (active) {
          setError(loadError.message);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      active = false;
    };
  }, [jobPath, token]);

  async function createWorkspace() {
    if (!job.matter_path) {
      setError('This job is not linked to a matter, so an OA workspace cannot be created.');
      return;
    }

    setCreatingWorkspace(true);
    setError('');
    setSuccess('');

    try {
      const title = buildWorkspaceTitle(job);
      const createResponse = await fetch(`/++api++${job.matter_path}`, {
        method: 'POST',
        headers: authHeaders({
          Accept: 'application/json',
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          '@type': 'Office Action',
          title,
        }),
      });
      if (!createResponse.ok) {
        throw new Error(await extractError(createResponse));
      }
      const created = await createResponse.json();
      const oaPath = normalizeContentPath(created['@id'] || '');
      const normalizedOA = normalizeOfficeAction(oaPath, created);

      const patchResponse = await fetch(`/++api++${job.path}`, {
        method: 'PATCH',
        headers: authHeaders({
          Accept: 'application/json',
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          office_action_path: oaPath,
        }),
      });
      if (!patchResponse.ok) {
        throw new Error(await extractError(patchResponse));
      }

      setJob((current) => ({
        ...current,
        office_action_path: oaPath,
      }));
      setOA(normalizedOA);
      setSuccess('OA workspace created.');
    } catch (createError) {
      setError(createError.message);
    } finally {
      setCreatingWorkspace(false);
    }
  }

  async function saveWorkspace(event) {
    event.preventDefault();
    if (!oa.path) {
      setError('Create the OA workspace first.');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`/++api++${oa.path}`, {
        method: 'PATCH',
        headers: authHeaders({
          Accept: 'application/json',
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          title: oa.title || buildWorkspaceTitle(job),
          oa_date: oa.oa_date || null,
          drafting_date: oa.drafting_date || null,
          original_text: oa.original_text,
          translation: oa.translation,
          confirmed_translation: oa.confirmed_translation,
          response_draft: oa.response_draft,
          client_report_draft: oa.client_report_draft,
          oa_profile_path: oa.oa_profile_path || null,
        }),
      });
      if (!response.ok) {
        throw new Error(await extractError(response));
      }

      let nextOA = oa;
      if (response.status !== 204) {
        const payload = await response.json();
        nextOA = normalizeOfficeAction(oa.path, payload);
      }
      setOA((current) => ({ ...current, ...nextOA }));
      setSuccess('OA workspace saved.');
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setSaving(false);
    }
  }

  async function persistOfficeAction(updates, successMessage) {
    if (!oa.path) {
      return;
    }

    const requestPayload = {
      title: (updates.title ?? oa.title) || buildWorkspaceTitle(job),
      oa_date: (updates.oa_date ?? oa.oa_date) || null,
      drafting_date: (updates.drafting_date ?? oa.drafting_date) || null,
      original_text: updates.original_text ?? oa.original_text,
      translation: updates.translation ?? oa.translation,
      confirmed_translation:
        updates.confirmed_translation ?? oa.confirmed_translation,
      response_draft: updates.response_draft ?? oa.response_draft,
      client_report_draft:
        updates.client_report_draft ?? oa.client_report_draft,
      oa_profile_path: updates.oa_profile_path ?? oa.oa_profile_path ?? null,
    };

    const response = await fetch(`/++api++${oa.path}`, {
      method: 'PATCH',
      headers: authHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(requestPayload),
    });
    if (!response.ok) {
      throw new Error(await extractError(response));
    }

    let nextOA = {
      ...oa,
      ...requestPayload,
      oa_date: requestPayload.oa_date || '',
      drafting_date: requestPayload.drafting_date || '',
    };
    if (response.status !== 204) {
      const payload = await response.json();
      nextOA = normalizeOfficeAction(oa.path, payload);
    }
    setOA(nextOA);
    if (successMessage) {
      setSuccess(successMessage);
    }
  }

  async function runCleanPreview() {
    if (!oa.path) {
      setError('Create and save the OA workspace first.');
      return;
    }
    if (!oa.original_text.trim()) {
      setError('Paste the OA text first.');
      return;
    }
    setCleaning(true);
    setError('');
    setSuccess('');
    try {
      const response = await fetch('/++api++/@oa-clean-preview', {
        method: 'POST',
        headers: authHeaders({
          Accept: 'application/json',
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          text: oa.original_text,
          oa_profile_path: oa.oa_profile_path || '',
          ...scopePayload(),
        }),
      });
      if (!response.ok) {
        throw new Error(await extractError(response));
      }
      const payload = await response.json();
      const cleaned = payload.cleaned || oa.original_text;
      setOA((current) => ({
        ...current,
        original_text: cleaned,
      }));
      await persistOfficeAction(
        { original_text: cleaned },
        'Clean preview applied and saved.',
      );
    } catch (cleanError) {
      setError(cleanError.message);
    } finally {
      setCleaning(false);
    }
  }

  async function runTranslate() {
    if (!oa.path) {
      setError('Create and save the OA workspace first.');
      return;
    }
    if (!oa.original_text.trim()) {
      setError('Save or enter the OA text first.');
      return;
    }
    setTranslating(true);
    setError('');
    setSuccess('');
    try {
      const response = await fetch('/++api++/@oa-translate', {
        method: 'POST',
        headers: authHeaders({
          Accept: 'application/json',
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          text: oa.original_text,
          ...scopePayload(),
        }),
      });
      if (!response.ok) {
        throw new Error(await extractError(response));
      }
      const payload = await response.json();
      const translation = payload.translation || oa.translation;
      const updatedRecentPrompts = storePromptMemory(
        promptScopeKey,
        translationAdjustmentPrompt,
        'recent',
      );
      setRecentAdjustmentPrompts(updatedRecentPrompts);
      setOA((current) => ({
        ...current,
        translation,
      }));
      await persistOfficeAction(
        { translation },
        payload.notice || 'Translation generated and saved.',
      );
    } catch (translateError) {
      setError(translateError.message);
    } finally {
      setTranslating(false);
    }
  }

  async function runRefineTranslation() {
    if (!oa.original_text.trim()) {
      setError('Save or enter the OA text first.');
      return;
    }
    if (!oa.translation.trim()) {
      setError('Generate or enter the translation first.');
      return;
    }
    if (!translationAdjustmentPrompt.trim()) {
      setError('Enter an adjustment prompt first.');
      return;
    }
    setRefiningTranslation(true);
    setError('');
    setSuccess('');
    try {
      const response = await fetch('/++api++/@oa-refine-translate', {
        method: 'POST',
        headers: authHeaders({
          Accept: 'application/json',
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          original_text: oa.original_text,
          current_translation: oa.translation,
          instruction: translationAdjustmentPrompt,
          oa_profile_path: oa.oa_profile_path || '',
          ...scopePayload(true),
        }),
      });
      if (!response.ok) {
        throw new Error(await extractError(response));
      }
      const payload = await response.json();
      const translation = payload.translation || oa.translation;
      setOA((current) => ({
        ...current,
        translation,
      }));
      await persistOfficeAction(
        { translation },
        'Translation refined and saved.',
      );
    } catch (refineError) {
      setError(refineError.message);
    } finally {
      setRefiningTranslation(false);
    }
  }

  async function confirmTranslation() {
    if (!oa.path) {
      setError('Create and save the OA workspace first.');
      return;
    }
    const confirmedTranslation =
      oa.confirmed_translation.trim() || oa.translation.trim();
    if (!confirmedTranslation) {
      setError('Generate or enter the translation first.');
      return;
    }
    setConfirmingTranslation(true);
    setError('');
    setSuccess('');
    try {
      const response = await fetch('/++api++/@oa-confirm-translation', {
        method: 'POST',
        headers: authHeaders({
          Accept: 'application/json',
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          translation: confirmedTranslation,
          oa_profile_path: oa.oa_profile_path || '',
          ...scopePayload(true),
        }),
      });
      if (!response.ok) {
        throw new Error(await extractError(response));
      }
      const payload = await response.json();
      setOA((current) => ({
        ...current,
        confirmed_translation:
          payload.confirmed_translation || confirmedTranslation,
        oa_profile_path: payload.oa_profile_path || current.oa_profile_path,
      }));
      setSuccess('Confirmed translation saved.');
    } catch (confirmError) {
      setError(confirmError.message);
    } finally {
      setConfirmingTranslation(false);
    }
  }

  async function generateClientReport() {
    if (!oa.path) {
      setError('Create and save the OA workspace first.');
      return;
    }
    if (!oa.original_text.trim() || !oa.confirmed_translation.trim()) {
      setError('Confirm the translation before generating the client report.');
      return;
    }
    setGeneratingClientReport(true);
    setError('');
    setSuccess('');
    try {
      const response = await fetch('/++api++/@oa-generate-client-report', {
        method: 'POST',
        headers: authHeaders({
          Accept: 'application/json',
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          original_text: oa.original_text,
          confirmed_translation: oa.confirmed_translation,
          oa_profile_path: oa.oa_profile_path || '',
          ...scopePayload(true),
        }),
      });
      if (!response.ok) {
        throw new Error(await extractError(response));
      }
      const payload = await response.json();
      const clientReportDraft =
        payload.client_report_draft || oa.client_report_draft;
      setOA((current) => ({
        ...current,
        client_report_draft: clientReportDraft,
      }));
      await persistOfficeAction(
        { client_report_draft: clientReportDraft },
        'Client report draft generated and saved.',
      );
    } catch (reportError) {
      setError(reportError.message);
    } finally {
      setGeneratingClientReport(false);
    }
  }

  async function exportDocx(endpoint, kind) {
    if (!oa.path) {
      setError('Create and save the OA workspace first.');
      return;
    }
    setExportingDocx(kind);
    setError('');
    setSuccess('');
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: authHeaders({
          Accept: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          oa_profile_path: oa.oa_profile_path || '',
          confirmed_translation: oa.confirmed_translation,
          client_report_draft: oa.client_report_draft,
          oa_date: oa.oa_date || '',
          drafting_date: oa.drafting_date || '',
          ...scopePayload(true),
        }),
      });
      if (!response.ok) {
        throw new Error(await extractError(response));
      }
      const payload = await response.json();
      const blob = decodeBase64ToBlob(
        payload.data_base64 || '',
        payload.mime_type ||
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      );
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = payload.filename || `${kind}.docx`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(url);
      setSuccess('DOCX exported.');
    } catch (exportError) {
      setError(exportError.message);
    } finally {
      setExportingDocx('');
    }
  }

  async function loadJPODocuments() {
    if (!job.matter_path) {
      setError('This job is not linked to a matter.');
      return;
    }
    setLoadingJPODocuments(true);
    setError('');
    setSuccess('');
    try {
      const response = await fetch('/++api++/@oa-jpo-documents', {
        method: 'POST',
        headers: authHeaders({
          Accept: 'application/json',
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({ matter_path: job.matter_path }),
      });
      if (!response.ok) {
        throw new Error(await extractError(response));
      }
      const payload = await response.json();
      setJPODocuments(payload.documents || []);
      setJob((current) => ({
        ...current,
        application_number: payload.application_number || current.application_number,
      }));
      setSuccess('JPO documents loaded.');
    } catch (listError) {
      setError(listError.message);
    } finally {
      setLoadingJPODocuments(false);
    }
  }

  async function saveApplicationNumber() {
    if (!job.matter_path) {
      setError('This job is not linked to a matter.');
      return;
    }
    const normalized = applicationNumberDraft.trim();
    if (!normalized) {
      setError('Enter an application number first.');
      return;
    }
    setSavingApplicationNumber(true);
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`/++api++${job.matter_path}`, {
        method: 'PATCH',
        headers: authHeaders({
          Accept: 'application/json',
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          application_number: normalized,
        }),
      });
      if (!response.ok) {
        throw new Error(await extractError(response));
      }
      setJob((current) => ({
        ...current,
        application_number: normalized,
      }));
      setSuccess('Application number saved to the matter.');
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setSavingApplicationNumber(false);
    }
  }

  async function importJPODocument(fetchNumber, documentNumber, documentDate = '') {
    if (!job.matter_path) {
      setError('This job is not linked to a matter.');
      return;
    }
    setImportingJPODocument(documentNumber);
    setError('');
    setSuccess('');
    try {
      const response = await fetch('/++api++/@oa-jpo-fetch', {
        method: 'POST',
        headers: authHeaders({
          Accept: 'application/json',
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          matter_path: job.matter_path,
          fetch_number: fetchNumber,
          document_number: documentNumber,
        }),
      });
      if (!response.ok) {
        throw new Error(await extractError(response));
      }
      const payload = await response.json();
      const cleaned = stripImportedDraftDateHeader(payload.cleaned || oa.original_text);
      const nextOADate = documentDate || oa.oa_date;
      const nextDraftingDate = payload.drafting_date || oa.drafting_date;
      setOA((current) => ({
        ...current,
        oa_date: nextOADate,
        drafting_date: nextDraftingDate,
        original_text: cleaned,
      }));
      await persistOfficeAction(
        {
          original_text: cleaned,
          oa_date: nextOADate,
          drafting_date: nextDraftingDate,
        },
        'Office action text imported from JPO and saved.',
      );
    } catch (importError) {
      setError(importError.message);
    } finally {
      setImportingJPODocument('');
    }
  }

  async function runDraft() {
    const draftTranslation =
      oa.confirmed_translation.trim() || oa.translation.trim();
    if (!oa.original_text.trim() || !draftTranslation) {
      setError('Both OA text and translation are required before drafting.');
      return;
    }
    setDrafting(true);
    setError('');
    setSuccess('');
    try {
      const response = await fetch('/++api++/@oa-draft', {
        method: 'POST',
        headers: authHeaders({
          Accept: 'application/json',
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          original_text: oa.original_text,
          translation: draftTranslation,
          ...scopePayload(true),
        }),
      });
      if (!response.ok) {
        throw new Error(await extractError(response));
      }
      const payload = await response.json();
      const responseDraft = payload.response_draft || oa.response_draft;
      setOA((current) => ({
        ...current,
        response_draft: responseDraft,
      }));
      await persistOfficeAction(
        { response_draft: responseDraft },
        'Response draft generated and saved.',
      );
    } catch (draftError) {
      setError(draftError.message);
    } finally {
      setDrafting(false);
    }
  }

  return (
    <main className="patent-entry patent-entry--oa">
      <section className="patent-entry__shell">
        <AppNav currentPath="/jobs" />
        <header className="patent-entry__hero">
          <p className="patent-entry__eyebrow">OA workspace</p>
          {job.matter_reference_code ? (
            <p className="patent-entry__context-chip">
              Matter {job.matter_reference_code}
            </p>
          ) : null}
          <h1>{job.title || 'Office action workspace'}</h1>
          <p className="patent-entry__note">
            A staged workspace for OA text, translation, client report drafting, and internal response planning.
          </p>
          <div className="patent-entry__actions patent-entry__actions--row">
            <Link className="patent-entry__ghost" to={`/job-editor?path=${encodeURIComponent(job.path || jobPath)}`}>
              Back to job
            </Link>
            <Link className="patent-entry__ghost" to="/oa-profiles">
              Manage OA profiles
            </Link>
          </div>
        </header>

        {loading ? <p className="patent-entry__note">Loading OA workspace…</p> : null}
        {error && !loading ? <p className="patent-entry__error">{error}</p> : null}
        {success ? <p className="patent-entry__success">{success}</p> : null}

        {!loading && !error ? (
          <>
            <section className="patent-entry__card patent-entry__card--steps">
              <div className="patent-steps">
                <StepChip label="1" done={Boolean(oa.original_text.trim())} text="OA text" />
                <StepChip
                  label="2"
                  done={Boolean(oa.confirmed_translation.trim() || oa.translation.trim())}
                  text="Translation"
                />
                <StepChip
                  label="3"
                  done={Boolean(oa.client_report_draft.trim())}
                  text="Client report"
                />
                <StepChip
                  label="4"
                  done={Boolean(oa.response_draft.trim())}
                  text="Internal draft"
                />
              </div>
            </section>

            <section className="patent-entry__card">
              <div className="patent-entry__section-header">
                <div>
                  <h2>Matter context</h2>
                  <p className="patent-entry__note">
                    The JPO fetch uses the application number stored on the linked matter.
                  </p>
                </div>
              </div>
              <label className="patent-entry__field">
                <span className="patent-entry__label">Application number</span>
                <input
                  className="patent-entry__input"
                  value={applicationNumberDraft}
                  onChange={(event) => setApplicationNumberDraft(event.target.value)}
                  placeholder="Example: 特願2020-123456"
                />
              </label>
              <div className="patent-entry__actions patent-entry__actions--row">
                <button
                  className="patent-entry__ghost"
                  type="button"
                  onClick={saveApplicationNumber}
                  disabled={savingApplicationNumber || !applicationNumberDraft.trim()}
                >
                  {savingApplicationNumber ? 'Saving…' : job.application_number ? 'Update application number' : 'Assign application number'}
                </button>
                {job.application_number ? (
                  <span className="patent-entry__note">Current: {job.application_number}</span>
                ) : (
                  <span className="patent-entry__note">No application number assigned yet.</span>
                )}
              </div>
              <label className="patent-entry__field">
                <span className="patent-entry__label">OA profile</span>
                <select
                  className="patent-entry__input"
                  value={oa.oa_profile_path}
                  disabled={loadingProfiles || !oaProfiles.length}
                  onChange={(event) =>
                    setOA((current) => ({
                      ...current,
                      oa_profile_path: event.target.value,
                    }))
                  }
                >
                  <option value="">No profile</option>
                  {oaProfiles.map((profile) => (
                    <option key={profile.path} value={profile.path}>
                      {profile.title}
                    </option>
                  ))}
                </select>
                <span className="patent-entry__help">
                  The selected profile supplies translation examples and optional DOCX templates.
                </span>
              </label>
            </section>

            {!oa.path ? (
              <section className="patent-entry__card">
                <div className="patent-entry__section-header">
                  <div>
                    <h2>Create OA workspace</h2>
                    <p className="patent-entry__note">
                      This job does not yet have a linked office action record.
                    </p>
                  </div>
                </div>
                <div className="patent-entry__actions patent-entry__actions--row">
                  <button
                    className="patent-entry__submit"
                    type="button"
                    onClick={createWorkspace}
                    disabled={creatingWorkspace}
                  >
                    {creatingWorkspace ? 'Creating…' : 'Create OA workspace'}
                  </button>
                </div>
              </section>
            ) : (
              <form className="patent-entry__form patent-entry__form--oa" onSubmit={saveWorkspace}>
                <section className="patent-entry__card">
                  <div className="patent-entry__section-header">
                    <div>
                      <h2>Step 1: OA text</h2>
                      <p className="patent-entry__note">
                        Paste or refine the JPO office action text, then save it into the linked record.
                      </p>
                    </div>
                    <div className="patent-entry__actions patent-entry__actions--row">
                      <button
                        className="patent-entry__ghost"
                        type="button"
                        onClick={loadJPODocuments}
                        disabled={loadingJPODocuments}
                      >
                        {loadingJPODocuments ? 'Loading JPO docs…' : 'Fetch from JPO'}
                      </button>
                      <button
                        className="patent-entry__ghost"
                        type="button"
                        onClick={runCleanPreview}
                        disabled={cleaning}
                      >
                        {cleaning ? 'Cleaning…' : 'Clean line breaks'}
                      </button>
                    </div>
                  </div>
                  {jpoDocuments.length ? (
                    <div className="patent-entry__jpo-documents">
                      <div className="patent-entry__label">JPO documents</div>
                      <p className="patent-entry__note">
                        Some JPO rows share the same fetch target. One representative import button stays enabled for each target, and duplicate rows are shown only for reference.
                      </p>
                      {sortedJPODocuments.map((document) => (
                        <div
                          key={`${document.fetch_number}-${document.document_number}`}
                          className="patent-entry__jpo-document"
                        >
                          <div>
                            <strong>{document.doc_type}</strong>
                            <div className="patent-entry__note">
                              {document.date_display} · {document.record_type}
                            </div>
                            {!document.downloadable ? (
                              <div className="patent-entry__note">
                                Listed for reference only. The current JPO text-fetch endpoint
                                does not support this document type.
                              </div>
                            ) : null}
                          </div>
                          <button
                            className="patent-entry__ghost"
                            type="button"
                            onClick={() =>
                              importJPODocument(
                                document.fetch_number,
                                document.document_number,
                                document.date_display || document.date || '',
                              )
                            }
                            disabled={
                              !document.downloadable ||
                              !document.importable ||
                              importingJPODocument === document.document_number
                            }
                          >
                            {!document.downloadable
                              ? 'Reference only'
                              : !document.importable
                                ? document.import_reason === 'same_fetch_target'
                                  ? 'Same JPO fetch target'
                                  : 'Not importable'
                              : importingJPODocument === document.document_number
                                ? 'Importing…'
                                : 'Import text'}
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : null}
                  <DateField
                    label="Mailing date"
                    value={oa.oa_date}
                    onChange={(value) => setOA((current) => ({ ...current, oa_date: value }))}
                  />
                  <DateField
                    label="Drafting date"
                    value={oa.drafting_date}
                    onChange={(value) =>
                      setOA((current) => ({ ...current, drafting_date: value }))
                    }
                  />
                  <TextAreaField
                    label="Office action original text"
                    rows={18}
                    value={oa.original_text}
                    onChange={(value) =>
                      setOA((current) => ({ ...current, original_text: value }))
                    }
                  />
                </section>

                <section className="patent-entry__card">
                  <div className="patent-entry__section-header">
                    <div>
                      <h2>Step 2: Translation</h2>
                      <p className="patent-entry__note">
                        Review the original and translated text side by side. Each pane scrolls independently so you can compare corresponding sections before confirming the final client-facing translation.
                      </p>
                    </div>
                    <div className="patent-entry__actions patent-entry__actions--row">
                      <button
                        className="patent-entry__ghost"
                        type="button"
                        onClick={runTranslate}
                        disabled={translating || refiningTranslation}
                      >
                        {translating ? 'Translating…' : 'Generate translation'}
                      </button>
                      <button
                        className="patent-entry__ghost"
                        type="button"
                        onClick={runRefineTranslation}
                        disabled={refiningTranslation || translating}
                      >
                        {refiningTranslation ? 'Refining…' : 'Refine translation'}
                      </button>
                    </div>
                  </div>
                  {translationBusy ? (
                    <div className="patent-entry__progress">
                      <div className="patent-entry__progress-title">
                        {translationBusyLabel}…
                      </div>
                      <p className="patent-entry__note">
                        Elapsed: {translationElapsedSeconds}s. Long OA translations can take over a minute.
                      </p>
                    </div>
                  ) : null}
                  <label className="patent-entry__field patent-entry__field--wide">
                    <span className="patent-entry__label">Adjustment prompt</span>
                    <textarea
                      className="patent-entry__textarea"
                      rows={4}
                      value={translationAdjustmentPrompt}
                      onChange={(event) =>
                        setTranslationAdjustmentPrompt(event.target.value)
                      }
                    />
                  </label>
                  <div className="patent-entry__prompt-palette">
                    <label className="patent-entry__field">
                      <span className="patent-entry__label">Recent prompts</span>
                      <select
                        className="patent-entry__input"
                        defaultValue=""
                        onChange={(event) => {
                          if (!event.target.value) {
                            return;
                          }
                          setTranslationAdjustmentPrompt(event.target.value);
                          setSuccess('Loaded a recent adjustment prompt.');
                          setError('');
                          event.target.value = '';
                        }}
                      >
                        <option value="">Select a recent prompt</option>
                        {recentAdjustmentPrompts.map((prompt, index) => (
                          <option key={`recent-${index}-${prompt}`} value={prompt}>
                            {summarizePrompt(prompt)}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="patent-entry__field">
                      <span className="patent-entry__label">Saved presets</span>
                      <select
                        className="patent-entry__input"
                        defaultValue=""
                        onChange={(event) => {
                          if (!event.target.value) {
                            return;
                          }
                          setTranslationAdjustmentPrompt(event.target.value);
                          setSuccess('Loaded a saved adjustment preset.');
                          setError('');
                          event.target.value = '';
                        }}
                      >
                        <option value="">Select a saved preset</option>
                        {savedAdjustmentPresets.map((prompt, index) => (
                          <option key={`preset-${index}-${prompt}`} value={prompt}>
                            {summarizePrompt(prompt)}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <div className="patent-entry__actions patent-entry__actions--row">
                    <button
                      className="patent-entry__ghost"
                      type="button"
                      onClick={() => {
                        if (!recentAdjustmentPrompts.length) {
                          return;
                        }
                        setTranslationAdjustmentPrompt(recentAdjustmentPrompts[0]);
                        setSuccess('Loaded the last adjustment prompt.');
                        setError('');
                      }}
                      disabled={!recentAdjustmentPrompts.length}
                    >
                      Reuse last
                    </button>
                    <button
                      className="patent-entry__ghost"
                      type="button"
                      onClick={() => {
                        const updatedPresets = storePromptMemory(
                          promptScopeKey,
                          translationAdjustmentPrompt,
                          'presets',
                        );
                        setSavedAdjustmentPresets(updatedPresets);
                        setSuccess('Saved the current adjustment prompt as a preset.');
                        setError('');
                      }}
                      disabled={!translationAdjustmentPrompt.trim()}
                    >
                      Save as preset
                    </button>
                  </div>
                  <p className="patent-entry__note">
                    Prompt recall is stored locally in this browser. Profile-specific
                    prompts fall back to your global prompt history when needed.
                  </p>
                  <div className="patent-entry__compare">
                    <label className="patent-entry__field patent-entry__field--wide">
                      <span className="patent-entry__label">Original text</span>
                      <textarea
                        className="patent-entry__textarea patent-entry__textarea--compare"
                        rows={22}
                        value={oa.original_text}
                        readOnly
                      />
                    </label>
                    <TextAreaField
                      label="Translation"
                      rows={22}
                      className="patent-entry__textarea--compare patent-entry__textarea--translation"
                      value={oa.translation}
                      onChange={(value) =>
                        setOA((current) => ({ ...current, translation: value }))
                      }
                    />
                  </div>
                  <TextAreaField
                    label="Confirmed translation"
                    rows={18}
                    value={oa.confirmed_translation}
                    onChange={(value) =>
                      setOA((current) => ({
                        ...current,
                        confirmed_translation: value,
                      }))
                    }
                  />
                  <div className="patent-entry__actions patent-entry__actions--row">
                    <button
                      className="patent-entry__ghost"
                      type="button"
                      onClick={confirmTranslation}
                      disabled={confirmingTranslation}
                    >
                      {confirmingTranslation
                        ? 'Confirming…'
                        : 'Confirm current translation'}
                    </button>
                    <button
                      className="patent-entry__ghost"
                      type="button"
                      onClick={() =>
                        exportDocx(
                          '/++api++/@oa-export-translation-docx',
                          'translation',
                        )
                      }
                      disabled={
                        exportingDocx === 'translation' ||
                        !oa.confirmed_translation.trim()
                      }
                    >
                      {exportingDocx === 'translation'
                        ? 'Exporting…'
                        : 'Generate translation DOCX'}
                    </button>
                  </div>
                </section>

                <section className="patent-entry__card">
                  <div className="patent-entry__section-header">
                    <div>
                      <h2>Step 3: Client report</h2>
                      <p className="patent-entry__note">
                        Generate the separate client-facing report from the saved OA text and confirmed translation.
                      </p>
                    </div>
                    <div className="patent-entry__actions patent-entry__actions--row">
                      <button
                        className="patent-entry__ghost"
                        type="button"
                        onClick={generateClientReport}
                        disabled={generatingClientReport}
                      >
                        {generatingClientReport
                          ? 'Generating report…'
                          : 'Generate client report'}
                      </button>
                      <button
                        className="patent-entry__ghost"
                        type="button"
                        onClick={() =>
                          exportDocx(
                            '/++api++/@oa-export-client-report-docx',
                            'client-report',
                          )
                        }
                        disabled={
                          exportingDocx === 'client-report' ||
                          !oa.client_report_draft.trim()
                        }
                      >
                        {exportingDocx === 'client-report'
                          ? 'Exporting…'
                          : 'Generate client report DOCX'}
                      </button>
                    </div>
                  </div>
                  <TextAreaField
                    label="Client report draft"
                    rows={22}
                    value={oa.client_report_draft}
                    onChange={(value) =>
                      setOA((current) => ({
                        ...current,
                        client_report_draft: value,
                      }))
                    }
                  />
                </section>

                <section className="patent-entry__card">
                  <div className="patent-entry__section-header">
                    <div>
                      <h2>Step 4: Internal response draft</h2>
                      <p className="patent-entry__note">
                        Generate the internal attorney response strategy draft from the saved OA text and the latest confirmed or working translation.
                      </p>
                    </div>
                    <button
                      className="patent-entry__ghost"
                      type="button"
                      onClick={runDraft}
                      disabled={drafting}
                    >
                      {drafting ? 'Drafting…' : 'Generate internal draft'}
                    </button>
                  </div>
                  <TextAreaField
                    label="Internal response draft"
                    rows={22}
                    value={oa.response_draft}
                    onChange={(value) =>
                      setOA((current) => ({ ...current, response_draft: value }))
                    }
                  />
                </section>

                <div className="patent-entry__actions patent-entry__actions--row">
                  <button
                    className="patent-entry__submit"
                    type="submit"
                    disabled={saving}
                  >
                    {saving ? 'Saving…' : 'Save OA workspace'}
                  </button>
                  <Link className="patent-entry__ghost" to={`/job-editor?path=${encodeURIComponent(job.path || jobPath)}`}>
                    Back to job
                  </Link>
                </div>
              </form>
            )}
          </>
        ) : null}
      </section>
    </main>
  );
}

function StepChip({ label, text, done = false, muted = false }) {
  const className = [
    'patent-step',
    done ? 'patent-step--done' : '',
    muted ? 'patent-step--muted' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={className}>
      <span className="patent-step__badge">{done ? '✓' : label}</span>
      <span className="patent-step__text">{text}</span>
    </div>
  );
}

function DateField({ label, value, onChange }) {
  return (
    <label className="patent-entry__field">
      <span className="patent-entry__label">{label}</span>
      <input
        className="patent-entry__input"
        type="date"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function TextAreaField({ label, value, onChange, rows = 6, className = '' }) {
  return (
    <label className="patent-entry__field patent-entry__field--wide">
      <span className="patent-entry__label">{label}</span>
      <textarea
        className={['patent-entry__textarea', className].filter(Boolean).join(' ')}
        rows={rows}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function normalizeContentPath(path) {
  if (!path) {
    return '';
  }
  const origin =
    typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
  try {
    return new URL(path, origin).pathname;
  } catch {
    return path;
  }
}

function normalizeJob(path, payload) {
  return {
    path,
    title: payload.title || '',
    job_kind: payload.job_kind || '',
    status: payload.status || 'triage',
    application_number:
      payload.parent?.application_number ||
      '',
    matter_reference_code:
      payload.parent?.matter_reference_code ||
      payload.parent?.matter_reference ||
      '',
    matter_title: payload.parent?.title || '',
    matter_path: normalizeContentPath(payload.parent?.['@id'] || ''),
    office_action_path: normalizeContentPath(payload.office_action_path || ''),
  };
}

function normalizeOfficeAction(path, payload) {
  const { body, draftingDate } = splitImportedDraftDate(payload.original_text || '');
  return {
    path,
    title: payload.title || '',
    oa_date: payload.oa_date || '',
    drafting_date: payload.drafting_date || draftingDate || '',
    original_text: body,
    translation: payload.translation || '',
    confirmed_translation: payload.confirmed_translation || '',
    response_draft: payload.response_draft || '',
    client_report_draft: payload.client_report_draft || '',
    oa_profile_path: normalizeContentPath(payload.oa_profile_path || ''),
  };
}

function buildWorkspaceTitle(job) {
  const matter = job.matter_reference_code ? ` ${job.matter_reference_code}` : '';
  return `Office Action${matter}`;
}

function splitImportedDraftDate(text) {
  const body = String(text || '');
  const match = body.match(/^起案日:\s*(\d{4}-\d{2}-\d{2})\s*\n\n?/u);
  if (!match) {
    return { body, draftingDate: '' };
  }
  return {
    body: body.replace(/^起案日:\s*\d{4}-\d{2}-\d{2}\s*\n\n?/u, ''),
    draftingDate: match[1],
  };
}

function stripImportedDraftDateHeader(text) {
  return splitImportedDraftDate(text).body;
}

function decodeBase64ToBlob(base64Value, mimeType) {
  const binary = window.atob(base64Value || '');
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new Blob([bytes], { type: mimeType });
}

async function extractError(response) {
  try {
    const payload = await response.json();
    if (Array.isArray(payload)) {
      return payload.map((item) => item.message || item.error).filter(Boolean).join(', ');
    }
    return (
      payload.message ||
      payload.error?.message ||
      (typeof payload.error === 'string' ? payload.error : '') ||
      `Request failed with status ${response.status}`
    );
  } catch {
    return `Request failed with status ${response.status}`;
  }
}

function getPromptScopeKey(profilePath) {
  return normalizeContentPath(profilePath || '') || '__global__';
}

function loadPromptMemoryStore() {
  if (typeof window === 'undefined') {
    return { recent: {}, presets: {} };
  }

  try {
    const parsed = JSON.parse(window.localStorage.getItem(OA_PROMPT_MEMORY_KEY) || '{}');
    return {
      recent:
        parsed && typeof parsed.recent === 'object' && parsed.recent ? parsed.recent : {},
      presets:
        parsed && typeof parsed.presets === 'object' && parsed.presets
          ? parsed.presets
          : {},
    };
  } catch {
    return { recent: {}, presets: {} };
  }
}

function savePromptMemoryStore(store) {
  if (typeof window === 'undefined') {
    return;
  }
  window.localStorage.setItem(OA_PROMPT_MEMORY_KEY, JSON.stringify(store));
}

function normalizePromptList(prompts, limit) {
  const unique = [];
  (prompts || []).forEach((prompt) => {
    const normalized = String(prompt || '').trim();
    if (!normalized || unique.includes(normalized)) {
      return;
    }
    unique.push(normalized);
  });
  return unique.slice(0, limit);
}

function readPromptMemory(scopeKey) {
  const store = loadPromptMemoryStore();
  const recent = normalizePromptList(
    [
      ...(store.recent[scopeKey] || []),
      ...(scopeKey !== '__global__' ? store.recent.__global__ || [] : []),
    ],
    8,
  );
  const presets = normalizePromptList(
    [
      ...(store.presets[scopeKey] || []),
      ...(scopeKey !== '__global__' ? store.presets.__global__ || [] : []),
    ],
    12,
  );
  return { recent, presets };
}

function storePromptMemory(scopeKey, prompt, kind) {
  const normalizedPrompt = String(prompt || '').trim();
  if (!normalizedPrompt || !['recent', 'presets'].includes(kind)) {
    return readPromptMemory(scopeKey)[kind] || [];
  }

  const limits = { recent: 8, presets: 12 };
  const store = loadPromptMemoryStore();
  const scopedValues = store[kind][scopeKey] || [];
  store[kind][scopeKey] = normalizePromptList(
    [normalizedPrompt, ...scopedValues],
    limits[kind],
  );
  savePromptMemoryStore(store);
  return readPromptMemory(scopeKey)[kind];
}

function summarizePrompt(prompt) {
  const collapsed = String(prompt || '').replace(/\s+/gu, ' ').trim();
  if (collapsed.length <= 72) {
    return collapsed || '(empty)';
  }
  return `${collapsed.slice(0, 69)}...`;
}
